import React from 'react'
import { useLocation } from 'react-router'
import { useDispatch } from 'react-redux'
import dayjs from '@mobi/utils/date/extendedDayjs'
import { useLogon } from '@core/Utils/hooks'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { TrustedDevicePrompt } from '@mobi/component-library/Login/TrustedDevicePrompt'
import { HasLoggedIn } from '@core/State/UserAccount/userAccountDriver'
import { useAppSelector } from '@core/Store/hooks'
import { AppRoutes } from '@core/AppRoutes'
import { getTrustedDeviceDetails } from '@core/Areas/Login/Store/selectors'
import { recordTrustedDevicePrompt, resetTrustedDevicePrompt } from '@core/Areas/Login/Store'
import { useTrustDevice } from '@core/Areas/Settings/Components/MFASettings/Hooks/useTrustDevice'
import { trackMfaEvent } from '@core/Areas/Settings/Components/MFASettings/Helpers/helpers'

export const TrustedDeviceTray = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const trustedDevices = useAppSelector(getTrustedDeviceDetails)
  const { lastSuccessfulLogin, trustedDeviceLastPrompted } = useAppSelector(state => state.login)
  const [isOpen, setIsOpen] = React.useState(false)
  const { accountNumber } = useLogon()
  const { trustDevice } = useTrustDevice()

  const displayedLessThan30DaysAgo = React.useMemo(
    () => dayjs().diff(dayjs(trustedDeviceLastPrompted), 'day') < 30,
    [trustedDeviceLastPrompted]
  )

  React.useEffect(() => {
    const subscription = HasLoggedIn.signal$.subscribe(({ data }) => {
      const { accountNumber } = data
      // Only display on home screen
      if (pathname !== AppRoutes.Home) return
      // Only display if last login used MFA
      if (!lastSuccessfulLogin?.withMfa) return
      // Only display if there are trusted devices
      if (trustedDevices[accountNumber] === undefined) return
      // Only display if user hasn't already 'trusted' their device
      if (trustedDevices[accountNumber].isRemembered) return
      // Only display if last prompt was more than 30 days ago
      if (displayedLessThan30DaysAgo) return

      setIsOpen(true)
    })
    return () => subscription?.dispose()
  }, [
    pathname,
    lastSuccessfulLogin,
    displayedLessThan30DaysAgo,
    trustedDevices,
    accountNumber,
    dispatch,
  ])

  const onTrustDevice = async () => {
    // Close the tray, and perform the trusted device update in the background
    // Provide success/failure feedback via a toast notification
    setIsOpen(false)

    if (accountNumber) {
      await trustDevice(accountNumber)
      dispatch(resetTrustedDevicePrompt())
      trackMfaEvent({ interaction_type: 'Trust this device', otpFlow: 'Login', skipped: false })
    }
  }

  const onSkip = () => {
    setIsOpen(false)
    dispatch(recordTrustedDevicePrompt())
    trackMfaEvent({ interaction_type: 'Skip for now', otpFlow: 'Login', skipped: true })
  }

  return (
    <Tray
      isOpen={isOpen}
      headerTitle={null}
      shouldCoverFullscreen={false}
      dismissStatus='disallowed'
    >
      <TrustedDevicePrompt onSkip={onSkip} onTrustDevice={onTrustDevice} />
    </Tray>
  )
}
